import React from 'react';

import { TChangePasswordFormValues } from 'types/state';
import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { useServices } from 'hooks/useServices';
import { useForm } from 'hooks/useForm';
import { setCustomTimeout } from 'utils/setCustomTimeout';
import { DONE_TIMEOUT_DURATION } from 'utils/constants';
import { useAsyncActionViewScreen } from 'hooks/useAsyncViewScreen';
import { createChangePasswordFormValidator } from 'utils/createChangePasswordFormValidator';
import { useServiceState } from 'hooks/useServiceState';
import { SidebarModalSectionComponent } from 'design/sidebarModal';
import { TextComponent } from 'design/text/textComponents';
import { FormControlComponent } from 'design/formControl';
import { MarginComponent } from 'design/margin';
import { ModalPressControlComponent } from 'design/modalPressControl';
import { CustomFieldsetComponent } from 'design/customFieldset/customFieldsetComponents';
import { CustomInputComponent } from 'design/customInput/customInputComponents';

type TProps = {
  clientId: number;
  onCancel(): void;
  onSubmitDidSucceed(): void;
};

const initialValues: TChangePasswordFormValues = {
  actualPassword: '',
  nextPassword: '',
  nextPasswordRepeat: '',
};

export const ClientDetailsPasswordEditor = (props: TProps) => {
  const { clientId, onCancel, onSubmitDidSucceed } = props;

  const intl = useIntlDictionary();

  const { updateClientPassword: updateClientPasswordService } = useServices();

  // Валидаторы формы.
  const validator = React.useMemo(
    () =>
      createChangePasswordFormValidator(intl, { requireActualPassword: false }),
    [intl]
  );

  // Состояние формы, функция для валидирования,
  // хэндлер изменений инпута.
  const { values, validation, validate, onChange } = useForm({
    initialValues,
    validator,
  });

  // Состояние запроса на обновление пароля,
  // функция отправки запроса.
  const [updatePasswordState, updatePassword] = useServiceState(
    updateClientPasswordService
  );

  // Эффект следит за состоянием запроса на обновление
  // пароля и вызывает соответствующий колбэк с задержкой,
  // чтобы успел посветиться индикатор успеха.
  React.useEffect(() => {
    if (updatePasswordState && updatePasswordState.resolved) {
      return setCustomTimeout(() => {
        onSubmitDidSucceed();
      }, DONE_TIMEOUT_DURATION);
    }
  }, [values, updatePasswordState, onSubmitDidSucceed]);

  // Хэндлер сабмита формы, валидирует форму и в
  // случае успеха отправляет запрос на обновление пароля.
  const onSubmit = React.useCallback(() => {
    validate(() => {
      updatePassword({ clientId, nextPassword: values.nextPassword });
    });
  }, [validate, updatePassword, clientId, values.nextPassword]);

  // Актуальный экран.
  const screen = useAsyncActionViewScreen(updatePasswordState);

  return (
    <>
      <SidebarModalSectionComponent
        theme="head"
        content={
          <TextComponent size="mpp" weight="medium">
            {intl.clientDetails.passwordEditor.title}
          </TextComponent>
        }
        actions={null}
        navigation={null}
      />
      <FormControlComponent onSubmit={onSubmit}>
        <SidebarModalSectionComponent
          content={
            <CustomFieldsetComponent disabled={screen !== 'ACTIVE'}>
              <CustomInputComponent
                value={values.nextPassword}
                errorMessage={validation.nextPassword}
                type="password"
                name="nextPassword"
                placeholder={intl.nextPasswordInputPlaceholder}
                onChange={onChange}
              />
              <CustomInputComponent
                value={values.nextPasswordRepeat}
                errorMessage={validation.nextPasswordRepeat}
                type="password"
                name="nextPasswordRepeat"
                placeholder={intl.nextPasswordRepeatInputPlaceholder}
                onChange={onChange}
              />
            </CustomFieldsetComponent>
          }
          actions={
            <>
              <ModalPressControlComponent theme="primary" type="submit">
                {intl.saveChangesButton}
              </ModalPressControlComponent>
              <MarginComponent top={20} />
              <ModalPressControlComponent
                theme="secondary"
                type="button"
                onPress={onCancel}
              >
                {intl.cancelButton}
              </ModalPressControlComponent>
            </>
          }
          navigation={null}
        />
      </FormControlComponent>
    </>
  );
};
